import Image from "next/image";

const POSTER_MIN_RATIO = 0.85;
const POSTER_MAX_RATIO = 1.2;

type Mode = "cover" | "letterbox";

function pickMode(
  dimensions: { width: number; height: number } | null | undefined,
): Mode {
  if (!dimensions) return "cover";
  const r = dimensions.width / dimensions.height;
  if (r >= POSTER_MIN_RATIO && r <= POSTER_MAX_RATIO) return "letterbox";
  return "cover";
}

interface CoverImageProps {
  src: string;
  alt: string;
  containerRatio: number;
  dimensions?: { width: number; height: number } | null;
  priority?: boolean;
  sizes?: string;
}

function withSmartCrop(src: string, ratio: number): string {
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}_cfRatio=${ratio}`;
}

/**
 * Renders an image inside a fixed-size container based on the source's
 * aspect ratio:
 *  - 0.85 <= r <= 1.2 (poster zone) uses object-contain over a blurred copy of
 *    the same image, so poster-style uploads stay fully visible
 *  - otherwise uses object-cover with saliency-aware crop at Cloudflare
 *    (gravity=auto via the `_cfRatio` query marker recognized by the
 *    custom image loader)
 *
 * Without dimensions, falls back to plain object-cover (no smart crop).
 */
export function CoverImage({
  src,
  alt,
  containerRatio,
  dimensions,
  priority = false,
  sizes = "100vw",
}: CoverImageProps) {
  const mode = pickMode(dimensions);

  if (mode === "cover") {
    const coverSrc = dimensions ? withSmartCrop(src, containerRatio) : src;
    return (
      <Image
        src={coverSrc}
        alt={alt}
        fill
        className="object-cover w-full rounded-sm"
        sizes={sizes}
        priority={priority}
      />
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm">
      <Image
        src={src}
        alt=""
        aria-hidden
        fill
        className="object-cover scale-110 blur-2xl"
        sizes={sizes}
        priority={priority}
      />
      <div className="absolute inset-0 bg-white/20" />
      <Image
        src={src}
        alt={alt}
        fill
        className="object-contain"
        sizes={sizes}
        priority={priority}
      />
    </div>
  );
}
